import React, { useEffect, useId, useRef, useState } from 'react';
import { Loader2Icon, MapPinIcon, CheckCircle2Icon, AlertTriangleIcon, InfoIcon } from 'lucide-react';
import type { AddressValue, Lang } from '../../types/funnel';
import { fetchAddress, fetchSuggestions, newSessionToken, placesEnabled, type PlaceSuggestion } from '../../utils/places';
import { validateAddress } from '../../utils/bookingApi';
import { digitsOnly } from '../../utils/format';
import { TextField } from './TextField';
const copy = {
  en: {
    street: 'Street address',
    unit: 'Apt / unit (optional)',
    building: 'Building, complex or community name (optional)',
    city: 'City',
    state: 'State',
    zip: 'ZIP',
    searching: 'Searching addresses…',
    none: 'No matching addresses. Keep typing, or enter it manually — our team will review it.',
    error: 'Address suggestions are unavailable right now. Type your address; our team will review it.',
    typed: 'Not verified — our team reviews this address.',
    typedManual: 'Address suggestions aren’t connected yet. Type the full address; our team reviews it.',
    selected: 'Selected from Google suggestions — not yet validated.',
    validated: 'Address checked.',
    review: 'We couldn’t fully confirm this address. Our team will review it with you.',
    zipConflict: (g: string, m: string) => `Google lists ZIP ${g} for this address. You entered ${m}.`,
    useZip: (z: string) => `Use ${z}`,
    keepZip: (z: string) => `Keep ${z}`,
    suggestion: 'Suggested correction:',
    useSuggestion: 'Use suggestion',
    keepMine: 'Keep mine'
  },
  es: {
    street: 'Dirección',
    unit: 'Apto / unidad (opcional)',
    building: 'Edificio, complejo o comunidad (opcional)',
    city: 'Ciudad',
    state: 'Estado',
    zip: 'Código postal',
    searching: 'Buscando direcciones…',
    none: 'No hay direcciones que coincidan. Siga escribiendo o ingrésela manualmente; nuestro equipo la revisará.',
    error: 'Las sugerencias de dirección no están disponibles ahora. Escriba su dirección; nuestro equipo la revisará.',
    typed: 'Sin verificar: nuestro equipo revisa esta dirección.',
    typedManual: 'Las sugerencias de dirección aún no están conectadas. Escriba la dirección completa; nuestro equipo la revisa.',
    selected: 'Seleccionada de las sugerencias de Google; aún no validada.',
    validated: 'Dirección revisada.',
    review: 'No pudimos confirmar completamente esta dirección. Nuestro equipo la revisará con usted.',
    zipConflict: (g: string, m: string) => `Google indica el código postal ${g} para esta dirección. Usted escribió ${m}.`,
    useZip: (z: string) => `Usar ${z}`,
    keepZip: (z: string) => `Mantener ${z}`,
    suggestion: 'Corrección sugerida:',
    useSuggestion: 'Usar sugerencia',
    keepMine: 'Mantener la mía'
  }
};
interface AddressFieldsProps {
  value: AddressValue;
  onChange: (patch: Partial<AddressValue>) => void;
  lang: Lang;
  zipError?: string;
}
type Pending = {
  kind: 'zip';
  google: string;
} | {
  kind: 'fix';
  street: string;
  city: string;
  state: string;
  zip: string;
} | null;
export function AddressFields({
  value,
  onChange,
  lang,
  zipError
}: AddressFieldsProps) {
  const c = copy[lang];
  const uid = useId();
  const enabled = placesEnabled();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<PlaceSuggestion[]>([]);
  const [state, setState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [active, setActive] = useState(-1);
  const [pending, setPending] = useState<Pending>(null);
  const reqId = useRef(0);
  const token = useRef<unknown>(null);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const onStreet = (v: string) => {
    onChange({
      street: v,
      placeId: null,
      status: 'typed'
    });
    setPending(null);
    if (!enabled) return;
    window.clearTimeout(timer.current);
    if (v.trim().length < 3) {
      setItems([]);
      setOpen(false);
      return;
    }
    const id = ++reqId.current;
    setState('loading');
    setOpen(true);
    timer.current = window.setTimeout(async () => {
      try {
        if (!token.current) token.current = await newSessionToken();
        const res = await fetchSuggestions(v, token.current);
        if (id !== reqId.current) return; // discard stale responses
        setItems(res);
        setActive(-1);
        setState('idle');
      } catch {
        if (id !== reqId.current) return;
        setItems([]);
        setState('error');
      }
    }, 200);
  };
  const select = async (s: PlaceSuggestion) => {
    setOpen(false);
    reqId.current++;
    setState('loading');
    try {
      const a = await fetchAddress(s);
      token.current = null; // session ends at selection
      const typedZip = value.zip;
      onChange({
        street: a.street || s.main,
        city: a.city,
        state: a.state,
        placeId: a.placeId,
        status: 'selected',
        ...(typedZip ? {} : {
          zip: a.zip
        })
      });
      if (typedZip && a.zip && typedZip !== a.zip) setPending({
        kind: 'zip',
        google: a.zip
      });
      setState('idle');
      const v = await validateAddress({
        street: a.street,
        unit: value.unit,
        city: a.city,
        state: a.state,
        zip: typedZip || a.zip,
        placeId: a.placeId
      }).catch(() => null);
      if (v) {
        onChange({
          status: v.verdict
        });
        if (v.suggested) setPending({
          kind: 'fix',
          ...v.suggested
        });
      }
    } catch {
      setState('error');
    }
  };
  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || items.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(i => Math.min(items.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(i => Math.max(0, i - 1));
    } else if (e.key === 'Enter' && active >= 0) {
      e.preventDefault();
      void select(items[active]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };
  const status = value.status === 'validated' ? {
    icon: <CheckCircle2Icon className="h-4 w-4 text-lime-dark" data-id="element-376" data-mp="2cdubvi02o6lz" />,
    text: c.validated
  } : value.status === 'needs_review' ? {
    icon: <AlertTriangleIcon className="h-4 w-4 text-magenta-dark" data-id="element-377" data-mp="28ev8i1tzohwn" />,
    text: c.review
  } : value.status === 'selected' ? {
    icon: <MapPinIcon className="h-4 w-4 text-ink-500" data-id="element-378" data-mp="2hdp1li842q5g" />,
    text: c.selected
  } : {
    icon: <InfoIcon className="h-4 w-4 text-ink-500" data-id="element-379" data-mp="2hbvqnqqgtjrn" />,
    text: enabled ? c.typed : c.typedManual
  };
  const listId = `${uid}-list`;
  return <div className="space-y-3" data-id="element-380" data-mp="2r4baktlboif6">
      <div className="relative" data-id="element-381" data-mp="2r304uo8bh0lf">
        <label htmlFor={`${uid}-street`} className="mb-1.5 block text-sm font-semibold text-ink" data-id="element-382" data-mp="353ay1xx3mr9r">
          {c.street}
        </label>
        <div className="relative" data-id="element-383" data-mp="303ya19uhre5l">
          <input id={`${uid}-street`} role={enabled ? 'combobox' : undefined} aria-expanded={enabled ? open : undefined} aria-controls={enabled ? listId : undefined} aria-autocomplete={enabled ? 'list' : undefined} aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined} autoComplete="off" value={value.street} onChange={e => onStreet(e.target.value)} onKeyDown={onKey} onBlur={() => window.setTimeout(() => setOpen(false), 150)} onFocus={() => items.length > 0 && setOpen(true)} className="h-12 w-full rounded-lg border-2 border-ink-200 bg-white px-4 pr-10 text-base text-ink focus:border-ink focus:outline-none" data-id="element-384" data-mp="304zn1jv2iapy" />
          {state === 'loading' && <Loader2Icon className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 animate-spin text-ink-400" aria-label={c.searching} data-id="element-385" data-mp="2pmcm25v85iao" />}
        </div>
        {enabled && open && <ul id={listId} role="listbox" className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-ink-200" data-id="element-386" data-mp="2tq0vku3f2nyk">
            {state === 'loading' && items.length === 0 && <li className="px-4 py-3 text-sm text-ink-500" data-id="element-387" data-mp="ru8pbqp1natp">{c.searching}</li>}
            {state === 'error' && <li className="px-4 py-3 text-sm text-magenta-dark" data-id="element-388" data-mp="3bugeune2r7dc">{c.error}</li>}
            {state === 'idle' && items.length === 0 && <li className="px-4 py-3 text-sm text-ink-500" data-id="element-389" data-mp="322j4tjysylg7">{c.none}</li>}
            {items.map((s, i) => <li key={s.id} id={`${listId}-${i}`} role="option" aria-selected={i === active} onMouseDown={e => {
          e.preventDefault();
          void select(s);
        }} className={`flex cursor-pointer items-start gap-2 px-4 py-2.5 text-sm ${i === active ? 'bg-ink-100' : 'hover:bg-ink-100'}`} data-id="element-390" data-mp="2x494kuy80unq" data-mpi={i}>
                <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-ink-400" aria-hidden="true" data-id="element-391" data-mp="2e6oi1uixi86a" />
                <span data-id="element-392" data-mp="hbsdcy1fq53o">
                  <span className="font-semibold text-ink" data-id="element-393" data-mp="ha7it8f4em5t">{s.main}</span>
                  {s.secondary && <span className="block text-xs text-ink-500" data-id="element-394" data-mp="cbe79lje958a">{s.secondary}</span>}
                </span>
              </li>)}
          </ul>}
      </div>

      <div className="grid gap-3 sm:grid-cols-2" data-id="element-395" data-mp="oujhx697qh97">
        <TextField label={c.unit} value={value.unit} onChange={v => onChange({
        unit: v.slice(0, 20)
      })} data-id="element-396" data-mp="tn2xg8rgnmgn" />
        <TextField label={c.building} value={value.building} onChange={v => onChange({
        building: v.slice(0, 80)
      })} data-id="element-397" data-mp="ymfhymuv0hg2" />
      </div>
      <div className="grid grid-cols-[1fr_72px_110px] gap-3" data-id="element-398" data-mp="1wydgb7m51fy">
        <TextField label={c.city} value={value.city} onChange={v => onChange({
        city: v,
        placeId: null,
        status: 'typed'
      })} data-id="element-399" data-mp="5wyw681qvyst" />
        <TextField label={c.state} value={value.state} maxLength={2} onChange={v => onChange({
        state: v.toUpperCase().slice(0, 2),
        placeId: null,
        status: 'typed'
      })} data-id="element-400" data-mp="ava0k399th3n" />
        <TextField label={c.zip} inputMode="numeric" maxLength={5} value={value.zip} error={zipError} onChange={v => onChange({
        zip: digitsOnly(v).slice(0, 5),
        status: value.status === 'selected' ? 'selected' : 'typed'
      })} data-id="element-401" data-mp="1mbqji6k1afv9" />
      </div>

      {pending?.kind === 'zip' && <div role="status" className="rounded-lg bg-ink-100 p-3 text-sm text-ink" data-id="element-402" data-mp="2x22fie1nohd7">
          {c.zipConflict(pending.google, value.zip)}
          <div className="mt-2 flex gap-2" data-id="element-403" data-mp="36tgd2ga1r73p">
            <button type="button" onClick={() => {
          onChange({
            zip: pending.google
          });
          setPending(null);
        }} className="min-h-[40px] rounded-md bg-ink px-3 font-semibold text-white" data-id="element-404" data-mp="36vt37gpyknru">
              {c.useZip(pending.google)}
            </button>
            <button type="button" onClick={() => {
          onChange({
            status: 'needs_review'
          });
          setPending(null);
        }} className="min-h-[40px] rounded-md border-2 border-ink-200 bg-white px-3 font-semibold text-ink" data-id="element-405" data-mp="2j3m1e21grol1">
              {c.keepZip(value.zip)}
            </button>
          </div>
        </div>}
      {pending?.kind === 'fix' && <div role="status" className="rounded-lg bg-ink-100 p-3 text-sm text-ink" data-id="element-406" data-mp="2eym66ylaguu">
          {c.suggestion} <span className="font-semibold" data-id="element-407" data-mp="7d07kyjhms9h">{[pending.street, pending.city, pending.state, pending.zip].filter(Boolean).join(', ')}</span>
          <div className="mt-2 flex gap-2" data-id="element-408" data-mp="cjf2t2jw81zp">
            <button type="button" onClick={() => {
          onChange({
            street: pending.street,
            city: pending.city,
            state: pending.state,
            zip: pending.zip
          });
          setPending(null);
        }} className="min-h-[40px] rounded-md bg-ink px-3 font-semibold text-white" data-id="element-409" data-mp="ckgffbgk4wjj">
              {c.useSuggestion}
            </button>
            <button type="button" onClick={() => {
          onChange({
            status: 'needs_review'
          });
          setPending(null);
        }} className="min-h-[40px] rounded-md border-2 border-ink-200 bg-white px-3 font-semibold text-ink" data-id="element-410" data-mp="3uyftxwtkqu06">
              {c.keepMine}
            </button>
          </div>
        </div>}

      <p className="flex items-start gap-2 text-xs text-ink-600" data-id="element-411" data-mp="3gyesaf5hjijn">
        <span className="mt-px shrink-0" aria-hidden="true" data-id="element-412" data-mp="1j7vdmq4fn1jn">
          {status.icon}
        </span>
        {status.text}
      </p>
    </div>;
}